import { useCallback, useRef, useEffect } from "react";
import { useMst } from "../../../stores/main";

type TimelineWheelIntent =
  | { type: "zoom"; zoomFactor: number }
  | { type: "pan"; horizontalDelta: number; verticalDelta: number }
  | null;

export function getAnchoredScrollOffset({
  anchorX,
  scrollOffset,
  previousZoom,
  nextZoom,
}: {
  anchorX: number;
  scrollOffset: number;
  previousZoom: number;
  nextZoom: number;
}): number {
  if (previousZoom <= 0) return scrollOffset;

  const anchorFrame = (anchorX + scrollOffset) / previousZoom;
  return anchorFrame * nextZoom - anchorX;
}

export function getTimelineWheelIntent({
  ctrlKey,
  metaKey,
  shiftKey,
  deltaX,
  deltaY,
}: Pick<
  WheelEvent,
  "ctrlKey" | "metaKey" | "shiftKey" | "deltaX" | "deltaY"
>): TimelineWheelIntent {
  if (ctrlKey || metaKey) {
    return { type: "zoom", zoomFactor: deltaY > 0 ? 0.9 : 1.1 };
  }

  if (shiftKey || deltaX !== 0) {
    return {
      type: "pan",
      horizontalDelta: shiftKey ? deltaY : deltaX,
      verticalDelta: shiftKey ? 0 : deltaY,
    };
  }

  return null;
}

export function useTimelineZoom() {
  const { timelineStore } = useMst();
  const panelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const getWheelAnchorX = useCallback((event: WheelEvent) => {
    const tracksArea = panelRef.current?.querySelector<HTMLElement>(
      ".timeline-tracks-area"
    );
    if (!tracksArea) return 0;

    const rect = tracksArea.getBoundingClientRect();
    return Math.max(0, Math.min(rect.width, event.clientX - rect.left));
  }, []);

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      const container = containerRef.current;
      const intent = getTimelineWheelIntent(e);

      if (intent?.type === "zoom") {
        e.preventDefault();

        const previousZoom = timelineStore.zoom;
        const previousScrollOffset = timelineStore.scrollOffset;
        const anchorX = getWheelAnchorX(e);

        timelineStore.setZoom(previousZoom * intent.zoomFactor);
        timelineStore.setScrollOffset(
          getAnchoredScrollOffset({
            anchorX,
            scrollOffset: previousScrollOffset,
            previousZoom,
            nextZoom: timelineStore.zoom,
          })
        );
        return;
      }

      if (intent?.type === "pan") {
        // Any horizontal gesture inside the timeline should pan the timeline
        // instead of leaking to browser back/forward navigation.
        e.preventDefault();
        timelineStore.setScrollOffset(
          timelineStore.scrollOffset + intent.horizontalDelta
        );

        // Preserve diagonal two-finger scrolling by replaying the vertical
        // component manually after preventing the browser default.
        if (container && intent.verticalDelta !== 0) {
          container.scrollTop += intent.verticalDelta;
        }
      }
    },
    [getWheelAnchorX, timelineStore]
  );

  useEffect(() => {
    const handleCapturedWheel = (event: WheelEvent) => {
      const panel = panelRef.current;
      if (!panel) return;

      const target = event.target;
      if (!(target instanceof Node) || !panel.contains(target)) {
        return;
      }

      handleWheel(event);
    };

    document.addEventListener("wheel", handleCapturedWheel, {
      passive: false,
      capture: true,
    });
    return () => {
      document.removeEventListener("wheel", handleCapturedWheel, {
        capture: true,
      });
    };
  }, [handleWheel]);

  return {
    panelRef,
    containerRef,
    zoom: timelineStore.zoom,
    scrollOffset: timelineStore.scrollOffset,
    setZoom: timelineStore.setZoom.bind(timelineStore),
    setScrollOffset: timelineStore.setScrollOffset.bind(timelineStore),
    zoomIn: timelineStore.zoomIn.bind(timelineStore),
    zoomOut: timelineStore.zoomOut.bind(timelineStore),
    fitToView: timelineStore.fitToView.bind(timelineStore),
  };
}
